// noinspection ES6UnusedImports
import React, {Fragment, useEffect, useRef, useState} from 'react';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import ProductMessages from '../message-list';
import Logger from '@core/scripts/helper/logger';
import Glossary from "../../helper/glossary";
import ModelContainer from "@core/scripts/components/model-container";
import Landmark from "@core/scripts/components/landmark-adapter";
import GlossaryEntry from "../glossary/GlossaryEntry.jsx";
import BatteryDisposalNote from "./BatteryDisposalNote.jsx";
import DeclarationOfConformity from "./DeclarationOfConformity.jsx";
import Brand from "./Brand.jsx";
import Toggles from '@core/scripts/modules/toggles';
import {BottomSheet, Dialog, Link, Typography} from "@bonprix/design-system";
import Breakpoint from '@core/scripts/helper/breakpoint';
import {AnchorTarget, scrollTo} from "@product/helper/anchor-utils.ts";

const MOBILE_DEVICE_BREAKPOINTS = ['start', 'small'];
const DESKTOP_DEVICE_BREAKPOINTS = ['medium', 'large', 'xlarge', 'max'];

export default function Information({product, staticLinks, texts, style, item, itemOption}) {

    const [currentStyle, setCurrentStyle] = useState(style);
    const [currentItem, setCurrentItem] = useState(item);
    const [currentItemOption, setCurrentItemOption] = useState(itemOption);
    const [glossary, setGlossary] = useState(null);
    const [layerOpen, setLayerOpen] = useState(false);
    const [layerTitle, setLayerTitle] = useState(null);
    const [layerContent, setLayerContent] = useState(null);

    const glossifications = useRef(0);

    useEffect(() => {
        ModelContainer.resolve('glossary')
            .then(Glossary.create)
            .then(setGlossary)
            .catch((err) => Logger.warn({message: 'could not fetch glossary entries', error: err}));

        const subscription = MessageBroker.subscribe(ProductMessages.CHANGED, ({item, itemOption, style}) => {
            if (style !== undefined) {
                setCurrentStyle(style);
            }
            if (item !== undefined) {
                setCurrentItem(item);
            }
            if (itemOption !== undefined) {
                setCurrentItemOption(itemOption);
            }
        });

        return () => MessageBroker.unsubscribe(subscription);
    }, []);

    useEffect(() => {
        glossifications.current = 0;
    });

    const resolveLayerTitle = (infoPart) => {
        if (infoPart.value === 'BATTERY_DISPOSAL_NOTE') {
            return texts.battery_disposal_headline;
        }
        return infoPart.text;
    };

    const resolveLayerContent = (infoPart) => {
        if (infoPart.value === 'BATTERY_DISPOSAL_NOTE') {
            return <BatteryDisposalNote texts={texts} linkTarget={staticLinks.batteryDisposalLinkTarget} />;
        }
        if (infoPart.value === 'DECLARATION_OF_CONFORMITY') {
            return <DeclarationOfConformity texts={texts} linkTarget={staticLinks.declarationOfConformity} />;
        }
        return <Typography variant="body1"><div dangerouslySetInnerHTML={{__html: infoPart.value}} /></Typography>;
    };

    const openInfoPartLayer = (infoPart) => {
        setLayerTitle(resolveLayerTitle(infoPart));
        setLayerContent(resolveLayerContent(infoPart));
        setLayerOpen(true);
    };

    const scrollToProductDetails = () => scrollTo(AnchorTarget.SIZE_SELECTION);

    const renderInfoPart = (infoPart, index) => {
        return (
            <li key={'info-part' + index} className="product-information__detail">
                <Typography color="on-bg-mid" variant="body1">
                    {glossify(infoPart.name, Toggles.isEnabled('PDP_GLOSSARY_ON_SUBHEADLINE'))}:&nbsp;
                </Typography>{' '}
                {infoPart.plaintext && <Typography variant="body1">{glossify(infoPart.text)}</Typography>}
                {infoPart.link && (
                    <Link href={infoPart.value} target="_blank">{infoPart.text}</Link>
                )}
                {infoPart.anchor && (
                    <Link href={null} onClick={scrollToProductDetails}>
                        <span className="product-information__link">{infoPart.text}</span>
                    </Link>
                )}
                {infoPart.layer && (
                    <Link href={null} onClick={() => openInfoPartLayer(infoPart)}>
                        <span className="product-information__link">{infoPart.text}</span>
                    </Link>
                )}
                {infoPart.html && <Typography variant="body1"><span dangerouslySetInnerHTML={{__html: infoPart.text}}/></Typography>}
            </li>
        );
    };

    const renderGlossaryEntry = (glossaryLabel, glossaryEntry) => {
        glossifications.current++;
        return (
            <GlossaryEntry
                key={'info-parts-glossary-entry-' + glossifications.current}
                label={glossaryLabel}
                entry={glossaryEntry}
                onGlossaryLabelClicked={(label) => Landmark.sendClickLandmark(`adp_glossary\\${label}`)}
            />
        );
    };

    const renderNonGlossaryEntry = (text, index) => {
        return <span key={'info-parts-non-glossary-entry-' + index}>{text}</span>;
    };

    const glossify = (text, enabled = true) => {
        if (!glossary || !enabled) {
            return text;
        }

        return glossary.glossify(text, renderGlossaryEntry, renderNonGlossaryEntry);
    };

    const renderInformationHeadline = () => {
        if (product.shortDescription) {
            return product.shortDescription;
        }
        return product.name;
    };

    const infoPartsByName = {};
    currentItemOption?.infoParts.forEach(infoPart => infoPartsByName[infoPart.name] = infoPart);
    currentItem.infoParts.forEach(infoPart => {
        // infoPart should not be set if it was already set from currentItemOption
        // OR if there is a currentItemOption selected that does not have the current infoPart
        if (infoPart.name in infoPartsByName || (currentItemOption && infoPart.text === texts.bff_choose_size)) {
            return;
        }
        infoPartsByName[infoPart.name] = infoPart;
    });
    currentStyle.infoParts.forEach(infoPart => (infoPart.name in infoPartsByName) || (infoPartsByName[infoPart.name] = infoPart));
    product.infoParts.forEach(infoPart => (infoPart.name in infoPartsByName) || (infoPartsByName[infoPart.name] = infoPart));
    const infoParts = Object.values(infoPartsByName);
    infoParts.sort((left, right) => left.position - right.position);

    const showBottomSheet = Breakpoint.keyIsIn(MOBILE_DEVICE_BREAKPOINTS);
    const showDialog = Breakpoint.keyIsIn(DESKTOP_DEVICE_BREAKPOINTS);

    return (
        <div className="product-information__wrapper">
            <div className="product-information__headline">
                <Typography variant="h4" component="h2">{renderInformationHeadline()}</Typography>
            </div>
            <Brand brand={product.brand}></Brand>
            {product?.description && (
                <div className="product-information__description">
                    <Typography variant="body1">{glossify(product?.description)}</Typography>
                </div>
            )}
            <ul className="product-information__details">{infoParts.map(renderInfoPart)}</ul>

            {/* OPENS IN BOTTOM SHEET FOR MOBILE */}
            {showBottomSheet && (
                <BottomSheet
                    isOpen={layerOpen}
                    onClose={() => setLayerOpen(false)}
                    onSnap={() => setLayerOpen(false)}
                >
                    <div className="product-information__layer">
                        <Typography variant="bonprix-h4">{layerTitle}</Typography>
                        <Typography variant="body1">{layerContent}</Typography>
                    </div>
                </BottomSheet>
            )}

            {/* OPENS IN DIALOG FOR DESKTOP */}
            {showDialog && (
                <Dialog zIndex={1100} isOpen={layerOpen} onClose={() => setLayerOpen(false)}>
                    <Dialog.Title>
                        {layerTitle}
                    </Dialog.Title>
                    <Dialog.Content>
                        {layerContent}
                    </Dialog.Content>
                </Dialog>
            )}
        </div>
    );
}
